import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

/**
 * FAQ — editorial accordion. Only one panel open at a time.
 * The first item ("What is the ordering process?") is intentionally denser
 * and splits into two sub-sections + a 3-step guide for custom work.
 */
export default function Faq() {
  return (
    <div data-testid="faq-page" className="fade-up">
      <section className="max-w-[880px] mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-12 text-center">
        <h1 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl text-brand-dark leading-[1.1]">
          FAQs
        </h1>
      </section>

      <section className="max-w-[880px] mx-auto px-6 md:px-12 pb-32">
        <Accordion type="single" collapsible defaultValue="q1" className="border-t border-brand-border">
          {/* Q1 — Ordering process (two sub-sections + 3-step guide) */}
          <AccordionItem value="q1" data-testid="faq-q1" className="border-b border-brand-border">
            <AccordionTrigger className="py-6 font-serif text-lg md:text-xl text-left text-brand-dark hover:text-brand-accent">
              What is the ordering process?
            </AccordionTrigger>
            <AccordionContent className="pb-10 text-brand-neutral leading-relaxed space-y-10">
              <div>
                <p className="overline mb-4">Ordering Greeting Cards &amp; Paper Goods</p>
                <ul className="space-y-2 text-[15px] pl-1">
                  <li className="flex gap-3"><span className="text-brand-accent">·</span> Browse the collection and add your favourite cards and paper goods to your cart.</li>
                  <li className="flex gap-3"><span className="text-brand-accent">·</span> Submit your order through the website.</li>
                  <li className="flex gap-3"><span className="text-brand-accent">·</span> We'll review your order and send an invoice including any applicable VAT and shipping.</li>
                  <li className="flex gap-3"><span className="text-brand-accent">·</span> Once payment has been received, your order will be carefully prepared and either shipped, delivered locally (where available), or made available for collection.</li>
                </ul>
              </div>

              <div>
                <p className="overline mb-6">Custom Design &amp; Stationery Projects</p>

                <div className="space-y-8">
                  <StepBlock
                    label="Step 1"
                    title="Inquiry &amp; Quote"
                    body={[
                      "Complete the inquiry form with as much detail as possible.",
                      "Once submitted, we'll review your project and prepare a custom proposal based on your requirements—whether that's branded stationery, greeting cards, menus, event pieces, packaging or another bespoke creative project.",
                      "If you'd like to move forward, a signed agreement and a 50% deposit are required to secure your booking.",
                      "If you have questions or would like to discuss your project before committing, we're always happy to arrange a call.",
                    ]}
                  />
                  <StepBlock
                    label="Step 2"
                    title="Design &amp; Approval"
                    body={[
                      "Once your project is booked, the creative process begins.",
                      "We'll gather your final copy, brand assets and any inspiration you'd like us to reference before developing a design direction tailored specifically to your project.",
                      "Digital proofs will be shared via email for review, along with recommendations for materials and production methods where applicable.",
                      "Once revisions have been completed, a final proof will be sent for approval before production begins.",
                    ]}
                  />
                  <StepBlock
                    label="Step 3"
                    title="Production &amp; Delivery"
                    body={[
                      "After final approval, your project moves into production.",
                      "Production timelines generally range from 2–6 weeks, depending on the scope, complexity and production method.",
                      "The remaining balance is due prior to completion.",
                      "Finished pieces are carefully prepared for collection or shipped directly to you, ready to use or distribute.",
                    ]}
                  />
                </div>

                <p className="mt-8 pt-6 border-t border-brand-border text-[13px] italic text-brand-neutral">
                  Please review all proofs carefully before providing final approval. Once production has begun, changes or corrections cannot be made.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <QA id="q2" q="Do you ship throughout The Bahamas?" a={[
            "Yes. We ship throughout The Bahamas.",
            "Shipping costs are calculated based on your order and destination and will be included on your invoice before payment.",
            "For customers in Nassau, local delivery or collection is also available.",
          ]} />

          <QA id="q3" q="Do you ship internationally?" a={[
            "Yes, on a case-by-case basis.",
            "If you're located outside The Bahamas, please get in touch before placing your order so we can provide a shipping quotation, estimated delivery timeframe and discuss the best shipping option for your location.",
          ]} />

          <QA id="q4" q="How do I become a wholesale stockist?" a={[
            "Complete the Wholesale Application through our website.",
            "Once approved, you'll receive access to wholesale pricing and be able to place orders through your wholesale account.",
          ]} />

          <QA id="q5" q="What are your wholesale minimums?">
            <p>Opening wholesale orders require:</p>
            <ul className="mt-3 space-y-1.5 pl-1">
              <li className="flex gap-3"><span className="text-brand-accent">·</span> A minimum of 5 cards per design</li>
              <li className="flex gap-3"><span className="text-brand-accent">·</span> A minimum opening order value of $150 before VAT and shipping</li>
            </ul>
          </QA>

          <QA id="q6" q="What are your reorder minimums?" a={[
            "There is no minimum reorder value after your opening order.",
            "However, all wholesale orders must continue to meet the minimum quantity of 5 cards per design.",
          ]} />

          <QA id="q7" q="Do you offer exclusive collections?" a={[
            "Yes.",
            "We love creating exclusive greeting card collections and bespoke stationery for select boutiques, hotels, resorts and retailers looking for something uniquely their own.",
            "If you'd like to discuss an exclusive collection, we'd love to hear from you.",
          ]} />

          <QA id="q8" q="Are your illustrations available as prints?" a={[
            "Yes.",
            "Many of our illustrations are available as fine art prints.",
            "Please get in touch for available sizes and pricing.",
            "A curated collection of prints will also be launching on the website before the end of the year.",
          ]} />

          <QA id="q9" q="Can I use your artwork for my business or project?" a={[
            "Please get in touch.",
            "All artwork remains the intellectual property of The Island Studio and may not be reproduced, redistributed or used commercially without written permission.",
            "Licensing opportunities, collaborations and custom illustration projects are always welcome.",
          ]} />

          <QA id="q10" q="What is your returns policy?" a={[
            "Due to the nature of our products, all sales are final.",
            "If your order arrives damaged or there is an issue with your purchase, please contact us as soon as possible. We'll always do our best to make it right.",
          ]} />
        </Accordion>
      </section>
    </div>
  );
}

/** Individual FAQ Q/A row — pass either `a` (array of paragraphs) or `children`. */
function QA({ id, q, a, children }) {
  return (
    <AccordionItem value={id} data-testid={`faq-${id}`} className="border-b border-brand-border">
      <AccordionTrigger className="py-6 font-serif text-lg md:text-xl text-left text-brand-dark hover:text-brand-accent">
        {q}
      </AccordionTrigger>
      <AccordionContent className="pb-10 text-brand-neutral leading-relaxed text-[15px] space-y-3">
        {a ? a.map((p, i) => <p key={i}>{p}</p>) : children}
      </AccordionContent>
    </AccordionItem>
  );
}

/** Step block used inside "Custom Design & Stationery Projects". */
function StepBlock({ label, title, body }) {
  return (
    <div>
      <div className="flex items-baseline gap-3">
        <span className="text-[11px] tracking-[0.22em] uppercase text-brand-accent font-medium">{label}</span>
        <span className="text-brand-border">—</span>
        <h4
          className="font-serif italic text-lg text-brand-dark"
          dangerouslySetInnerHTML={{ __html: title }}
        />
      </div>
      <div className="mt-3 space-y-2.5 text-[15px] leading-relaxed">
        {body.map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  );
}
